import { Analytics } from '@cobliteam/analytics';
import useBasicValueMaturity, { type MaturityItemState } from '@cobliteam/hooks/src/useBasicValueMaturity/useBasicValueMaturity';
import { useTranslation } from '@cobliteam/i18n';
import { DialogSetupGuideSuccess } from '@cobliteam/ui-business/components/dialogs/DialogSetupGuideShell/DialogSetupGuideSuccess';
import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { SETUP_GUIDE_ENTITY_FORMS, type SetupGuideEntityId } from './entityModalConfig';
import { getAdvancedSettingsRoute } from './setupGuideAdvancedSettings';

interface SetupGuideEntityModalProps {
  itemId: SetupGuideEntityId;
  fleetId?: string;
  /** Closes the whole guide (analytics + store close + dismissed persistence live in the caller). */
  onClose: () => void;
  /** Persists the guide as dismissed after the first successful creation (without closing). */
  onCreated: () => void;
  /** Invalidates the maturity counts so the just-created entity flips the item toward done. */
  refetchMaturity: () => void;
}

/** What the success screen needs even if the refetched maturity model drops the item from the list. */
type SuccessSnapshot = Pick<MaturityItemState, 'current' | 'target' | 'route'>;
type Phase = { name: 'form' } | { name: 'success'; createdCount: number; snapshot: SuccessSnapshot };

/**
 * Orchestrates one Setup Guide entity flow (AE-2443): the entity's creation form (900px) and, after
 * the mutation succeeds, the success modal (440px) with the updated progress and the shortcut to the
 * entity's full page. Swapping between two dialogs (instead of one modal with a "mode") keeps Radix
 * focus management correct on the size change.
 */
const SetupGuideEntityModal = ({ itemId, fleetId, onClose, onCreated, refetchMaturity }: SetupGuideEntityModalProps) => {
  const { t } = useTranslation('basic-value-onboarding');
  const history = useHistory();
  const [phase, setPhase] = useState<Phase>({ name: 'form' });

  // The maturity queries are shared react-query state (same keys the page/topbar read), so this adds
  // no extra fetching; after refetchMaturity() the current/target below update on their own.
  const maturity = useBasicValueMaturity();
  const item = maturity.items.find((candidate) => candidate.id === itemId);

  // External side effect on open: one "Opened" event per item (mirrors the source hook's Shown ref-guard).
  const trackedItemRef = useRef<SetupGuideEntityId | null>(null);
  useEffect(() => {
    if (trackedItemRef.current === itemId) return;
    trackedItemRef.current = itemId;
    Analytics.track('BasicValueEntityModalOpened', { fleetId, feature: itemId });
  }, [itemId, fleetId]);

  const EntityForm = SETUP_GUIDE_ENTITY_FORMS[itemId];

  const navigateToPage = (event: string, route: string) => {
    Analytics.track(event, { fleetId, feature: itemId, route });
    onClose();
    history.push(route);
  };

  if (phase.name === 'form') {
    // Item can vanish mid-flight (permission revoked / counts made it inapplicable) — nothing to render.
    if (!(item && EntityForm)) return null;
    return (
      <EntityForm
        fleetId={fleetId}
        item={item}
        onClose={onClose}
        // AE-2499: cai na página do fluxo com a criação já aberta, quando a página tem esse deep-link.
        onAdvancedSettings={() => navigateToPage('BasicValueEntityModalAdvancedSettingsClicked', getAdvancedSettingsRoute(itemId, item.route))}
        onSuccess={(createdCount) => {
          // Snapshot before the refetch resolves: the refreshed model may drop the item from the
          // list (e.g. counts made it inapplicable) and the success screen must survive that.
          const snapshot = { current: item.current, target: item.target, route: item.route };
          refetchMaturity();
          onCreated();
          setPhase({ name: 'success', createdCount, snapshot });
        }}
      />
    );
  }

  // Prefer the live item (the refetch keeps current/target fresh); when the item left the list,
  // fall back to the snapshot bumped by what this modal just created.
  const route = item?.route ?? phase.snapshot.route;
  const progress = item ?? { current: Math.min(phase.snapshot.current + phase.createdCount, phase.snapshot.target), target: phase.snapshot.target };

  // "Minha frota › Grupos" → intermediate crumbs are plain text; the leaf navigates to the page.
  const routeParts = t(`items.${itemId}.route`)
    .split('›')
    .map((part) => part.trim());

  return (
    <DialogSetupGuideSuccess
      open
      onClose={onClose}
      title={t(`entity_modal.${itemId}.success_title`)}
      progressLabel={t(`entity_modal.${itemId}.progress`, { current: progress.current, target: progress.target })}
      message={t(`entity_modal.${itemId}.success_message`)}
      pageLink={{
        items: routeParts.map((label, index) => (index === routeParts.length - 1 ? { label, href: route } : { label })),
        onNavigate: () => navigateToPage('BasicValueEntityModalSuccessPageLinkClicked', route),
      }}
      repeatAction={{
        label: t(`entity_modal.${itemId}.repeat`),
        onClick: () => {
          Analytics.track('BasicValueEntityModalRepeatClicked', { fleetId, feature: itemId });
          setPhase({ name: 'form' });
        },
      }}
      primaryAction={{ label: t('entity_modal.back_to_guide'), onClick: onClose }}
      data-testid='setup-guide-entity-success'
    />
  );
};

export default SetupGuideEntityModal;
